import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { useUser } from '@/components/auth-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { getFirebaseMessage } from './helpers';
import { forgotSchema, type ForgotValues } from './schema';

interface Props {
  onBack: () => void;
}

export function ForgotPasswordForm({ onBack }: Props) {
  const { sendResetEmail } = useUser();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<ForgotValues>({ resolver: zodResolver(forgotSchema) });

  async function onSubmit({ email }: ForgotValues) {
    const error = await sendResetEmail(email);
    if (error) {
      toast.error(getFirebaseMessage(error));
    } else {
      setSent(true);
    }
  }

  return (
    <Card className="w-full max-w-sm gap-4 py-8">
      <CardHeader>
        <CardTitle className="text-center text-xl">Reset password</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {sent ? (
          <div className="flex flex-col gap-4">
            <p className="text-muted-foreground text-center text-sm">
              We sent a reset link to{' '}
              <span className="text-foreground font-medium">
                {getValues('email')}
              </span>
              . Check your inbox.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={onBack}
              type="button"
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <>
            <p className="text-muted-foreground text-center text-sm">
              Enter your email and we&apos;ll send you a link to reset your
              password.
            </p>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-3"
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fp-email">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  data-testid="email-input"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-destructive text-xs">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="mt-1 w-full"
                data-loading={isSubmitting}
                data-testid="send-reset-button"
              >
                {isSubmitting && <Loader2Icon className="animate-spin" />}
                Send reset link
              </Button>
            </form>

            <button
              type="button"
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground text-center text-sm transition-colors"
            >
              Back to sign in
            </button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
